// DE QUAL PEDIDO — o campo que falta quando o compromisso nasce da Agenda ([DA-684]).
//
// ⚠️ **E' um CAMPO, nao um modal.** Nasceu como modal proprio (`ModalEscolherPedido`, removido) e
// durou uma manha: o gesto ficava com duas caixas em sequencia pro mesmo ato, e ele pediu uma so
// (*"Vamos atualizar para uma tela so"*, 08/09/2026). Agora mora dentro do modal de Andamento,
// acima da situacao — a ordem em que a pessoa pensa: com quem, em que pe, quando, o que ficou dito.
//
// ⚠️ **A celula ja disse QUANDO; aqui so' se escolhe o PEDIDO.** No banco todo compromisso pertence
// a um orcamento (`orcamento_andamentos.orcamento_id` e' NOT NULL) — nao existe compromisso solto.
// Foi a amarra que decidiu o tamanho da onda: compromisso sem pedido ("ligar pro fornecedor as
// 14h") mudaria o que a Agenda E', e ele escolheu explicitamente o caminho curto.
//
// ⚠️ **Busca a fila do zero, nao reaproveita a lista da tela**: com a aba Agenda aberta o
// `FilaRetomada` guarda `linhas: []` de proposito (a Agenda carrega por semana, nao por balde),
// entao ler dali abriria o campo vazio com 22 pedidos existindo.
//
// ⛔ So o balde `cliente`. O `sem_contato` nao tem numero pra retomar — marcar "falar as 10h" com
// quem nao se sabe como chamar e' agendar um compromisso que ninguem tem como cumprir.
import { useEffect, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api } from '../../lib/api';
import { useToast } from '../../components/Toast';
import type { LinhaFila } from './FilaRetomada';

export function SeletorPedido({ onEscolher }: { onEscolher: (linha: LinhaFila) => void }) {
  const toast = useToast();
  const [linhas, setLinhas] = useState<LinhaFila[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    let vivo = true;
    api.get<{ linhas: LinhaFila[] }>('/api/fila-retomada', { query: { balde: 'cliente' } })
      .then((r) => { if (vivo) setLinhas(r.linhas); })
      .catch((e: any) => toast.erro(e?.message ?? 'Falha ao carregar os pedidos'))
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [toast]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return linhas;
    return linhas.filter((l) =>
      (l.cliente ?? '').toLowerCase().includes(q)
      || l.numero.toLowerCase().includes(q)
      || (l.whatsapp_enviado ?? '').includes(q.replace(/\D/g, '')));
  }, [linhas, busca]);

  return (
    <div>
      <label className="block text-sm font-medium mb-2">De qual pedido?</label>

      <div className="relative mb-2">
        <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          className="input-base w-full"
          style={{ paddingLeft: 26 }}
          autoFocus
          placeholder="Cliente, numero do orcamento ou telefone"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          onKeyDown={(e) => {
            // Enter com UM resultado escolhe direto — quem digitou o nome do cliente ja decidiu.
            // Com dois ou mais nao adivinha: escolher pela pessoa marcaria compromisso no pedido
            // errado, e o erro so' apareceria na hora de ligar.
            if (e.key === 'Enter' && filtradas.length === 1) onEscolher(filtradas[0]!);
          }}
        />
      </div>

      {carregando ? (
        <p className="text-sm text-gray-400 py-4 text-center">Carregando...</p>
      ) : filtradas.length === 0 ? (
        <p className="text-sm text-gray-400 py-4 text-center">
          {linhas.length === 0
            ? 'Nenhum pedido aberto na janela. Compromisso so se marca em cima de um pedido.'
            : 'Nenhum pedido bate com essa busca.'}
        </p>
      ) : (
        // ⚠️ Altura fixa e rolagem PROPRIA: sem isso a lista de 22 empurra a situacao, a data e o
        // comentario pra fora da janela, e o modal vira uma tela de rolar procurando o Confirmar.
        <div style={{ maxHeight: 210, overflowY: 'auto', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 8 }}>
          {filtradas.map((l, idx) => (
            <button
              key={l.id}
              type="button"
              onClick={() => onEscolher(l)}
              className="w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-3"
              style={{
                backgroundColor: idx % 2 === 0 ? 'var(--grid-row-bg)' : 'var(--grid-row-alt-bg)',
                color: 'var(--grid-row-fg)',
                borderBottom: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              <span className="min-w-0">
                <span className="font-medium">{l.cliente ?? 'Sem nome'}</span>
                <span className="text-gray-400"> · {l.numero}</span>
                {/* O que ja esta marcado importa: remarcar por cima substitui o andamento
                    anterior, e quem clica precisa saber disso ANTES, nao depois de gravar. */}
                {l.situacao && <span className="text-gray-400"> · ja marcado: {l.situacao}</span>}
              </span>
              <span className="tabular-nums whitespace-nowrap">{formatMoeda(Number(l.total))}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
